import { createAsyncThunk, createSlice, PayloadAction, current } from '@reduxjs/toolkit';

import { fetchInsuranceOffers as fetchInsurance } from '../../lib/api';
import { ROUTES } from '../../lib/constants';
import {
  API_OPERATIONS,
  PRODUCTS,
  SESSION_KEYS
} from '../../lib/constants/appConstants';
import type { RootState } from '../../store/store';
import { Mapper } from '../../utils/mapper';
import { offerDefault, productDocumentsDefault } from '../defaults';
import {
  IPricingQuestion,
  IPricingStepConfig,
  IUpdatedOptionProps
} from '../../interfaces/EnquiryInterface';
import {
  AdditionalInputs,
  Coupon,
  ErrorStatus,
  LoadingStatus,
  OfferState,
  Package,
  PurchaseSteps,
  SaveAndSendOfferStatus,
  SelectionsAPI
} from '../../interfaces/Types';
import { APIHelper } from '../../utils/ApiHelper';
import { AppConstants } from '../../utils/AppConstants';
import { InsuranceHelper } from '../../utils/InsuranceHelper';
import { RoutingHelper } from '../../utils/RoutingHelper';
import { Validator } from '../../utils/Validator';
import { logMessage } from '../../utils/loggers';

const initialState: OfferState = {
  packages: [],
  pricingQuestions: [],
  offer: offerDefault,
  loading: 'idle',
  productDocuments: productDocumentsDefault,
  isMetaDataReceived: false,
  additionalInputs: [],
  modifiedInputs: {
    selections: [],
    isAdditionalInputsUpdated: false
  },
  saveAndSendOfferStatus: 'idle'
};

export const fetchInsuranceOffers = createAsyncThunk(
  'insurance/fetchInsuranceOffers',
  async (productName: string, { getState, rejectWithValue }) => {
    try {
      const insuranceState: any = getState();
      if (insuranceState.insurance.pricingQuestions.length > 0) {
        const payload = APIHelper.getCreateOfferPayload(
          productName,
          insuranceState.insurance.pricingQuestions
        );
        if (payload && !InsuranceHelper.isEmptyObject(payload)) {
          const response = await fetchInsurance(payload);
          if (response.isOk()) {
            return response.value;
          } else {
            return rejectWithValue(response.error);
          }
        } else {
          return rejectWithValue('');
        }
      }
    } catch (err) {
      return rejectWithValue('');
    }
  }
);

export const offerSlice = createSlice({
  name: 'offer',
  initialState,
  reducers: {
    setProductName: (state, action: PayloadAction<string>) => {
      state.offer.productName = action.payload;
    },

    setMetaDataStatus: (state, action: PayloadAction<boolean>) => {
      state.isMetaDataReceived = action.payload;
    },
    setCustomerName: (state, action: PayloadAction<string>) => {
      state.offer.customerName = action.payload;
    },
    setCouponInfo: (state, action: PayloadAction<Coupon>) => {
      state.offer.coupon = action.payload;
    },
    setChannelInfo: (state, action: PayloadAction<string>) => {
      state.offer.channel = action.payload;
    },
    updateInsurancePackageSelection: (
      state,
      action: PayloadAction<Package>
    ) => {
      Mapper.updateInsuranceSelection(state, action);
    },
    updatePlusPacketSelection: (state, action: PayloadAction<Package>) => {
      Mapper.updatePlusPacketSelection(state, action);
    },
    updateEnquirySelection: (
      state,
      action: PayloadAction<IUpdatedOptionProps>
    ) => {
      const currentQuestion = state.pricingQuestions.find(
        (item: any) => item.stepCode === action.payload.stepCode
      );
      const dependentQuestion = InsuranceHelper.getDependentQuestions(
        state.offer.productName,
        action.payload.questionType,
        currentQuestion
      );
      currentQuestion?.questions.forEach((question: any) => {
        if (question.APIKey === action.payload.questionType) {
          question.selectedValue = action.payload.selectedOption;
          question.isValid = action.payload.isValid;
          question.errorMessage = action.payload.errorMessage;
          question.isChecked = action.payload.isChecked;
        }
        if (dependentQuestion.type) {
          if (question.APIKey === dependentQuestion.type) {
            const status = Validator.validate(
              dependentQuestion.type,
              question.selectedValue,
              state.offer.productName,
              state.pricingQuestions,
              action.payload.selectedOption.toString()
            );
            question.isValid = status.isValid;
            question.errorMessage = status.errorMessage;
          }
        }
      });
    },
    updateAdditionalInputsSelection: (
      state,
      action: PayloadAction<IUpdatedOptionProps>
    ) => {
      const currentQuestion = state.pricingQuestions.find(
        (item: any) => item.stepCode === action.payload.stepCode
      );
      currentQuestion?.additionalQuestions?.forEach((question: any) => {
        if (question.APIKey === action.payload.questionType) {
          question.selectedValue = action.payload.selectedOption;
          question.isValid = action.payload.isValid;
        }
      });
    },
    addAdditionalInputs: (state, action: PayloadAction<AdditionalInputs>) => {
      state.additionalInputs?.push(action.payload);
    },
    updateAdditionalInputs: (
      state,
      action: PayloadAction<{
        updatedItem: AdditionalInputs;
        editIndex: number;
      }>
    ) => {
      if (state?.additionalInputs) {
        state?.additionalInputs.splice(
          action.payload.editIndex,
          1,
          action.payload.updatedItem
        );
      }
    },
    deleteAdditionalInput: (state, action: PayloadAction<number>) => {
      if (state?.additionalInputs) {
        state?.additionalInputs.splice(action.payload, 1);
      }
    },
    updateModifiedInputs: (
      state,
      action: PayloadAction<{
        selections?: SelectionsAPI;
        isAdditionalInputsUpdated?: boolean;
      }>
    ) => {
      if (action.payload.selections) {
        let isExist = false;
        state.modifiedInputs?.selections?.map((selection: SelectionsAPI) => {
          if (selection.key === action.payload?.selections?.key) {
            selection.value = action.payload?.selections.value;
            isExist = true;
          }
        });
        if (!isExist) {
          state.modifiedInputs?.selections?.push(action.payload.selections);
        }
      }
      if (action.payload.isAdditionalInputsUpdated) {
        state.modifiedInputs.isAdditionalInputsUpdated =
          !!action.payload.isAdditionalInputsUpdated;
      }
    },
    resetAdditionalInputSelections: (state, action: PayloadAction<string>) => {
      const stepQuestions = state.pricingQuestions?.find(
        (item: IPricingStepConfig) => item.stepCode === action.payload
      );
      if (
        stepQuestions?.additionalQuestions &&
        stepQuestions?.additionalQuestions.length > 0
      ) {
        stepQuestions?.additionalQuestions.map(
          (question: IPricingQuestion) =>
            (question.selectedValue =
              InsuranceHelper.getDefaultValueForAdditionalInputs(
                state.offer.productName,
                question
              ))
        );
      }
    },
    prefillAdditionalInputs: (state, action) => {
      const stepQuestions = state.pricingQuestions?.find(
        (item: IPricingStepConfig) => item.stepCode === action.payload.stepCode
      );
      if (
        stepQuestions?.additionalQuestions &&
        stepQuestions?.additionalQuestions.length > 0
      ) {
        stepQuestions?.additionalQuestions.map((question: IPricingQuestion) => {
          if (question.APIKey === 'extraFloors') {
            question.selectedValue = action.payload.item.type.value || '';
          }
          if (question.APIKey === 'extraBuildingArea') {
            question.selectedValue = action.payload.item.area.value || '';
          }
          if (question.APIKey === 'extraFront') {
            question.selectedValue = action.payload.item.energyType.value || '';
          }
          question.isValid = true;
        });
      }
    },

    onApiBegan: (state) => {
      state.loading = 'pending';
    },
    onApiSuccess: (state, action: PayloadAction<any>) => {
      // try {
      switch (action.payload.apiOperation) {
        case API_OPERATIONS.GET_APP_TEXTS:
          AppConstants.storeTextsInSession(
            SESSION_KEYS.APP_TEXT,
            action.payload.response[0]
          );
          AppConstants.storeTextsInSession(
            SESSION_KEYS.GENERIC_ILLUSTRATIONS,
            action.payload.response[1]
          );
          break;
        case API_OPERATIONS.GET_PRODUCT_CONFIG:
          Mapper.setinsuranceConfig(action.payload.response);
          break;
        case API_OPERATIONS.GET_QUESTION_CONFIG:
          Mapper.setPricingConfig(action.payload.response);
          break;
        case API_OPERATIONS.GET_TEMPLATE_DATA:
          if (
            action.payload?.response &&
            action.payload.response?.types.length > 0
          ) {
            state.pricingQuestions = Mapper.mapTemplateToQuestions(
              action.payload.response,
              state.offer
            );
          } else {
            state.offer.errorStatus = InsuranceHelper.buildErrorStatus(
              '',
              true
            );
            state.loading = 'failed';
            state.offer.objectDescription = '';
          }
          break;
        case API_OPERATIONS.UPDATE_INSURANACE_OFFER:
          if (
            action.payload &&
            !InsuranceHelper.isEmptyObject(action.payload) &&
            !action.payload?.response?.calculationStatus?.error
          ) {
            state.packages = Mapper.mapUpdatedOfferToPackages(
              action.payload.response,
              state
            );
            state.loading = 'succeeded';
            logMessage(
              `Offer updated successfully for Product : ${state.offer.productName}, policyNumber: ${state.offer.policyNumber}, customerNo: ${state.offer.customerNumber}`,
              {severity: 'INFO'}
            )
            const locationHash = RoutingHelper.getHashValueFromLocation();
            if (locationHash.includes(ROUTES.moreInputsPage)) {
              state.modifiedInputs = {
                selections: [],
                isAdditionalInputsUpdated: false
              };
              const nextPageUrl = RoutingHelper.resolveNextPageUrlPath(
                state.offer.productName,
                state.offer.policyNumber,
                state.offer.customerNumber,
                state.offer.productCode,
                state.offer.coupon.couponCode,
                state.offer.coupon.couponDiscountPercentage
              );
              window.location.href = `${window.location.pathname}#${nextPageUrl}`;
            }
          } else {
            state.offer.errorStatus = InsuranceHelper.buildErrorStatus(
              action.payload?.response,
              action.payload.response?.calculationStatus?.statusCode
            );
            state.loading = 'failed';
            state.offer.objectDescription = '';
          }
          break;
        case API_OPERATIONS.SAVE_OFFER:
          if (
            action.payload &&
            !InsuranceHelper.isEmptyObject(action.payload) &&
            !action.payload?.response?.calculationStatus?.error
          ) {
            const nextPageUrl = RoutingHelper.resolveNextPageUrlPath(
              state.offer.productName,
              state.offer.policyNumber,
              state.offer.customerNumber,
              state.offer.productCode,
              state.offer.coupon.couponCode,
              state.offer.coupon.couponDiscountPercentage
            );
            if (nextPageUrl.includes(ROUTES.paymentAppPath)) {
              window.location.href = nextPageUrl;
            } else {
              state.loading = 'succeeded';
              window.location.href = `${window.location.pathname}#${nextPageUrl}`;
            }
          } else {
            state.offer.errorStatus = InsuranceHelper.buildErrorStatus(
              action.payload?.response,
              action.payload?.response?.calculationStatus?.statusCode
            );
            state.loading = 'failed';
            state.offer.objectDescription = '';
          }
          break;
        case API_OPERATIONS.SAVE_AND_SEND_OFFER:
          if (
            action.payload &&
            !InsuranceHelper.isEmptyObject(action.payload) &&
            !action.payload?.response?.calculationStatus?.error
          ) {
            state.saveAndSendOfferStatus = 'succeeded';
            logMessage(
              `Saved and send offer successfully for Product : ${state.offer.productName}, policyNumber: ${state.offer?.policyNumber}, customerNo: ${state.offer.customerNumber}`,
              {severity: 'INFO'}
            )
          } else {
            state.saveAndSendOfferStatus = 'failed';
          }
          break;
        case API_OPERATIONS.GET_ALL_OFFERS:
          if (
            action.payload &&
            !InsuranceHelper.isEmptyObject(action.payload.response)
          ) {
            const apiResponse = action.payload.response;
            if (
              apiResponse?.offerResponse &&
              !InsuranceHelper.isEmptyObject(apiResponse.offerResponse) &&
              !apiResponse?.offerResponse?.calculationStatus?.error
            ) {
              state.packages = Mapper.mapOfferToPackages(
                state,
                apiResponse.offerResponse
              );
              state.offer.customerName =
                apiResponse.offerResponse.customerName || '';
              state.offer.customerNumber =
                apiResponse.offerResponse.customerNumber || '';
              state.offer.policyNumber = apiResponse.offerResponse.policyNumber;
              state.offer.agreementNumber =
                apiResponse.offerResponse.agreementNumber;
              state.offer.discountPercentage =
                apiResponse.offerResponse.discountPercentage;
              state.offer.contractCode = apiResponse.offerResponse.contractCode;
              state.offer.productCode = apiResponse.offerResponse.productCode;
              state.offer.objectDescription = apiResponse.offerResponse?.vehicleDetails || '';
              state.offer.salesChannel = apiResponse.offerResponse?.salesChannel || '';
              state.loading = 'succeeded';
            } else {
              state.offer.errorStatus = InsuranceHelper.buildErrorStatus(
                action.payload?.response?.offerResponse,
                apiResponse?.offerResponse?.calculationStatus?.statusCode
              );
              state.loading = 'failed';
            }
            if (
              apiResponse?.offerRequest &&
              !InsuranceHelper.isEmptyObject(apiResponse.offerRequest)
            ) {
              state.offer.channel = apiResponse.offerRequest?.channel || 'gjensidige';
              state.pricingQuestions = Mapper.mapSelectionsToQuestiions(
                state,
                apiResponse.offerRequest
              );
              const selectedPackage = Mapper.getSelectedPackage(state.packages);
              if (selectedPackage) {
                Mapper.updateQuestionsVisibility(
                  state.pricingQuestions,
                  selectedPackage,
                  state.offer.productName
                );
              }
            }
          }
          break;
        case API_OPERATIONS.GET_OFFER:
        case API_OPERATIONS.GET_OFFER_SUMMARY:
        case API_OPERATIONS.GET_SAVED_OFFER_SUMMARY:
        case API_OPERATIONS.RESUME_OFFER:
          if (
            action.payload &&
            !InsuranceHelper.isEmptyObject(action.payload.response)
          ) {
            const apiResponse = action.payload.response;
            if (
              apiResponse?.offerRequest &&
              !InsuranceHelper.isEmptyObject(apiResponse.offerRequest)
            ) {
              state.offer.channel = apiResponse.offerRequest?.channel || 'gjensidige';
              state.pricingQuestions = Mapper.mapSelectionsToQuestiions(
                state,
                apiResponse.offerRequest,
                action.payload.apiOperation
              );
              if (
                apiResponse?.offerResponse &&
                (action.payload.apiOperation ===
                  API_OPERATIONS.GET_SAVED_OFFER_SUMMARY || action.payload.apiOperation === API_OPERATIONS.RESUME_OFFER)
              ) {
                Mapper.setCustomerInfoInSession(
                  state.offer.productName,
                  apiResponse.offerRequest,
                  apiResponse.offerResponse
                );
                if (action.payload.apiOperation ===
                  API_OPERATIONS.GET_SAVED_OFFER_SUMMARY){
                    logMessage(
                      `User open saved offer : ${state.offer.productName}, policyNumber: ${apiResponse?.offerRequest?.policyNumber}, customerNo: ${apiResponse?.offerResponse?.customerNumber}`,
                      {severity: 'INFO'}
                    )
                  } else {
                    logMessage(
                      `User open resumed/prolonged offer : ${state.offer.productName}, policyNumber: ${apiResponse?.offerRequest?.policyNumber}, customerNo: ${apiResponse?.offerResponse?.customerNumber}`,
                      {severity: 'INFO'}
                    )
                  }
              }
            }
            if (
              apiResponse?.offerResponse &&
              !InsuranceHelper.isEmptyObject(apiResponse.offerResponse) &&
              !apiResponse?.offerResponse?.calculationStatus?.error
            ) {
              state.packages = Mapper.mapSelectedOffersToPackages(
                state,
                apiResponse.offerResponse
              );
              const selectedPackage = Mapper.getSelectedPackage(state.packages);
              if (selectedPackage) {
                Mapper.updateQuestionsVisibility(
                  state.pricingQuestions,
                  selectedPackage,
                  state.offer.productName
                );
              }
            } else {
              state.offer.errorStatus = InsuranceHelper.buildErrorStatus(
                apiResponse?.offerResponse || apiResponse,
                apiResponse?.offerResponse?.calculationStatus?.statusCode || apiResponse?.calculationStatus?.statusCode
              );
              state.loading = 'failed';
            }

            state.offer.customerName =
              apiResponse?.offerResponse?.customerName || '';
            state.offer.customerNumber =
              apiResponse?.offerResponse?.customerNumber || '';
            state.offer.policyNumber = apiResponse?.offerRequest?.policyNumber;
            state.offer.agreementNumber =
              apiResponse?.offerRequest?.agreementNumber;
            state.offer.objectDescription =
              apiResponse?.offerResponse?.vehicleDetails || '';
            state.offer.discountPercentage =
              apiResponse?.offerResponse?.discountPercentage;
            state.offer.contractCode = apiResponse?.offerResponse?.contractCode;
            state.offer.productCode = apiResponse?.offerResponse?.productCode;
            state.offer.coupon.couponCode =
              apiResponse?.offerResponse?.couponCode || '';
            state.offer.coupon.couponDiscountPercentage =
              apiResponse?.offerResponse?.couponDiscount || '';
            if (!apiResponse?.offerResponse?.calculationStatus?.error) {
              state.loading = 'succeeded';
            }
          } else {
            state.offer.errorStatus = InsuranceHelper.buildErrorStatus(
              action.payload,
              false
            );
            state.offer.objectDescription = '';
            state.loading = 'failed';
          }
          break;
        case API_OPERATIONS.GET_PRODUCT_DOCUMENTS:
          if (
            action.payload &&
            !InsuranceHelper.isEmptyObject(action.payload) &&
            !action.payload?.response?.calculationStatus?.error
          ) {
            state.productDocuments = action.payload.response;
          }
          break;
      }
      // } catch {
      //   state.offer.errorStatus = InsuranceHelper.buildErrorStatus(
      //     action.payload.response,
      //     true
      //   );
      //   state.loading = 'failed';
      //   state.offer.objectDescription = '';
      // }
    },
    onApiError: (state, action: PayloadAction<any>) => {
      //save additional packeges will call on background and no need to show error page if it fails
      if (
        action.payload.apiOperation !== API_OPERATIONS.SAVE_ADDITIONAL_PACKAGES
      ) {
        state.offer.errorStatus = InsuranceHelper.buildErrorStatus(
          action.payload,
          action.payload?.calculationStatus?.statusCode
        );
        state.loading = 'failed';
      }
    },
    updatePurchaseStatus: (
      state: OfferState,
      action: PayloadAction<PurchaseSteps>
    ) => {
      state.offer.purchaseSteps = action.payload;
    },
    updateErrorStatus: (
      state: OfferState,
      action: PayloadAction<ErrorStatus>
    ) => {
      state.offer.errorStatus = action.payload;
    },
    updateLoadingStatus: (
      state: OfferState,
      action: PayloadAction<LoadingStatus>
    ) => {
      state.loading = action.payload;
    },
    updateSaveAndSendStatus: (
      state: OfferState,
      action: PayloadAction<SaveAndSendOfferStatus>
    ) => {
      state.saveAndSendOfferStatus = action.payload;
    },
    updateCprNumber: (
      state: OfferState,
      action: PayloadAction<LoadingStatus>
    ) => {
      state.offer.cprNumber = action.payload;
    }
  },

  extraReducers: (builder) => {
    /* 
            *** 
                We have to wait until the get template api success to get the create offer request params
                Since api-middle-ware is invoking a reducer type on success call back, we may have to trigger create offer API
                from a reducer, which is not a best practice as it should not contain any asynchronous logic
                Also create offer is being triggered from useEffect and useAppSelector cannot be called from inside a hook.
                So we create an extra-reducer to handle this. 
            ***
            */
    builder.addCase(
      fetchInsuranceOffers.fulfilled,
      (state, action: PayloadAction<any>) => {
        if (
          action.payload &&
          !InsuranceHelper.isEmptyObject(action.payload) &&
          !action.payload?.calculationStatus?.error
        ) {
          state.packages = Mapper.mapOfferToPackages(state, action.payload);
          const selectedPackage = Mapper.getSelectedPackage(state.packages);
          if (selectedPackage) {
            Mapper.updateQuestionsVisibility(
              state.pricingQuestions,
              selectedPackage,
              state.offer.productName
            );
            Mapper.updatePakagesVisibility(
              state.packages,
              selectedPackage,
              state.offer.productName,
              state.offer.disabledPackages
            );
          }
          state.offer.customerName = action.payload.customerName || '';
          state.offer.customerNumber = action.payload.customerNumber;
          state.offer.policyNumber = action.payload.policyNumber;
          state.offer.agreementNumber = action.payload.agreementNumber;
          state.offer.objectDescription = action.payload.vehicleDetails || '';
          state.offer.discountPercentage = action.payload.discountPercentage;
          state.offer.contractCode = action.payload.contractCode;
          state.offer.productCode = action.payload.productCode;
          state.offer.channel = InsuranceHelper.getChannelInfo()?.toLowerCase() || 'gjensidige';
          state.loading = 'succeeded';
          logMessage(
            `Offer created successfully for Product : ${state.offer.productName}, policyNumber: ${action.payload.policyNumber}, customerNo: ${action.payload.customerNumber}`,
            {severity: 'INFO'}
          )

          /*
            Storing policy info in session so after create if user go back to more inputs page and refresh, a get offer call
            will trigger with the policy in session
          */

          const urlData = RoutingHelper.getUrlParams();
          const sessionKey = urlData['sessionKey'];
          if (urlData['sessionKey']) {
            RoutingHelper.storeOfferInfoInSession(
              sessionKey,
              action.payload.policyNumber,
              state.offer.productName,
              state.offer.cprNumber,
              action.payload.productCode,
              state.offer.coupon.couponCode,
              state.offer.coupon.couponDiscountPercentage,
              InsuranceHelper.getChannelInfo()
            );
          }
          if (state.offer.productName === PRODUCTS.VILLA) {
            RoutingHelper.removePropertyInfoFromSession();
          }
        } else {
          state.offer.errorStatus = InsuranceHelper.buildErrorStatus(
            action.payload,
            action.payload?.calculationStatus?.statusCode
          );
          state.offer.objectDescription = '';
          state.loading = 'failed';
        }
      }
    );
    builder.addCase(
      fetchInsuranceOffers.rejected,
      (state, action: PayloadAction<any>) => {
        state.offer.errorStatus = InsuranceHelper.buildErrorStatus(
          action.payload,
          action.payload?.calculationStatus?.statusCode
        );
        state.loading = 'failed';
        state.offer.objectDescription = '';
      }
    );
  }
});

export const getSelectedPackages = (state: RootState) =>
  state.insurance.packages.filter(
    (item: Package) => item.isEnabled && item.isSelected
  );

export const getProductName = (state: RootState) =>
  state.insurance?.offer?.productName;
export const getSaveAndSendStatus = (state: RootState) =>
  state.insurance?.saveAndSendOfferStatus;
export const getModifiedInputs = (state: RootState) =>
  state.insurance?.modifiedInputs;

export const {
  updateInsurancePackageSelection,
  updatePlusPacketSelection,
  updateEnquirySelection,
  updateAdditionalInputsSelection,
  updateAdditionalInputs,
  deleteAdditionalInput,
  addAdditionalInputs,
  resetAdditionalInputSelections,
  updateModifiedInputs,
  prefillAdditionalInputs,
  onApiBegan,
  onApiSuccess,
  onApiError,
  updatePurchaseStatus,
  updateErrorStatus,
  setProductName,
  updateLoadingStatus,
  updateCprNumber,
  setMetaDataStatus,
  setCustomerName,
  setCouponInfo,
  setChannelInfo,
  updateSaveAndSendStatus
} = offerSlice.actions;

export default offerSlice.reducer;
